import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShip,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faFolderPlus,
  faTable,
  faStar as faStarSolid,
  faXmark,
  faEye,
  faEyeSlash,
  faPenToSquare,
  faTrash,
  faFolder,
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import type { FavoritesMap, VesselPosition, BaseVesselData, StaticVesselData } from '../types/vessel';
import { getVesselName } from '../utils/vessel';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  favorites: FavoritesMap;
  hiddenGroups: Record<string, boolean>;
  emptyGroups?: string[];
  onToggleFavorite: (id: string) => void;
  onToggleGroupVisibility: (group: string) => void;
  onFlyToVessel: (id: string) => void;
  onSwitchToTable: () => void;
  onCreateGroup?: (name: string) => void;
  onMoveToGroup?: (vesselId: string, groupName: string) => void;
  onRenameGroup?: (oldName: string, newName: string) => void;
  onDeleteGroup?: (groupName: string) => void;
  vessels: Record<string, VesselPosition>;
  baseData: Record<string, BaseVesselData>;
  staticData: Record<string, StaticVesselData>;
}

export function Sidebar({
  collapsed,
  onToggle,
  favorites,
  hiddenGroups,
  emptyGroups = [],
  onToggleFavorite,
  onToggleGroupVisibility,
  onFlyToVessel,
  onSwitchToTable,
  onCreateGroup,
  onMoveToGroup,
  onRenameGroup,
  onDeleteGroup,
  vessels,
  baseData,
  staticData,
}: SidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [addToGroupVesselId, setAddToGroupVesselId] = useState<string | null>(null);
  const [addToGroupVesselName, setAddToGroupVesselName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('default');
  const [moveVesselId, setMoveVesselId] = useState<string | null>(null);
  const [moveVesselName, setMoveVesselName] = useState('');
  const [renameGroupName, setRenameGroupName] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return Object.entries(vessels)
      .filter(([id, v]) => {
        const name = getVesselName(v, id, baseData[id], staticData[id]).toLowerCase();
        return name.includes(query) || id.includes(query);
      })
      .slice(0, 8);
  }, [searchQuery, vessels, baseData, staticData]);

  const groupedFavorites = useMemo(() => {
    const groups: Record<string, Array<{ id: string; name: string; flag: string; type: string }>> = {};
    Object.entries(favorites).forEach(([id, fav]) => {
      const group = fav.group || 'default';
      if (!groups[group]) groups[group] = [];
      groups[group].push({ id, name: fav.name, flag: fav.flag, type: fav.type });
    });
    return groups;
  }, [favorites]);

  // Sort groups with 'default' (My Favorites) always first
  const sortedGroupEntries = useMemo(() => {
    const entries = Object.entries(groupedFavorites);
    return entries.sort(([a], [b]) => {
      if (a === 'default') return -1;
      if (b === 'default') return 1;
      return a.localeCompare(b);
    });
  }, [groupedFavorites]);

  const existingGroups = useMemo(() => {
    const fromFavorites = Object.keys(groupedFavorites);
    const all = new Set([...fromFavorites, ...emptyGroups]);
    // Sort with 'default' first
    return Array.from(all).sort((a, b) => {
      if (a === 'default') return -1;
      if (b === 'default') return 1;
      return a.localeCompare(b);
    });
  }, [groupedFavorites, emptyGroups]);

  const handleAddToGroup = () => {
    if (addToGroupVesselId && onToggleFavorite && onMoveToGroup) {
      // First add to favorites if not already
      if (!favorites[addToGroupVesselId]) {
        onToggleFavorite(addToGroupVesselId);
      }
      // Then move to selected group
      setTimeout(() => {
        onMoveToGroup(addToGroupVesselId!, selectedGroup);
      }, 50);
      setAddToGroupVesselId(null);
      setSelectedGroup('default');
    }
  };

  const handleQuickAdd = (vesselId: string) => {
    if (!favorites[vesselId]) {
      onToggleFavorite(vesselId);
    }
  };

  const openAddToGroupModal = (vesselId: string, vesselName: string) => {
    setAddToGroupVesselId(vesselId);
    setAddToGroupVesselName(vesselName);
    setSelectedGroup(existingGroups[0] || 'default');
  };

  const handleMoveToGroup = (groupName: string) => {
    if (moveVesselId && onMoveToGroup) {
      if (groupName === '__new__') {
        const newName = prompt('Enter new group name:');
        if (newName && newName.trim() && onCreateGroup) {
          onCreateGroup(newName.trim());
          onMoveToGroup(moveVesselId, newName.trim());
        }
      } else {
        onMoveToGroup(moveVesselId, groupName);
      }
      setMoveVesselId(null);
    }
  };

  const handleRenameGroup = () => {
    if (renameGroupName && renameValue.trim() && onRenameGroup) {
      onRenameGroup(renameGroupName, renameValue.trim());
      setRenameGroupName(null);
      setRenameValue('');
    }
  };

  const handleDeleteGroup = (groupName: string) => {
    if (onDeleteGroup && confirm(`Delete group "${groupName}"? All vessels in this group will be removed from favorites.`)) {
      onDeleteGroup(groupName);
    }
  };

  return (
    <>
      <aside style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '220px',
        height: '100%',
        background: '#f8f9fa',
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
        transition: 'transform 0.3s ease',
        transform: collapsed ? 'translateX(-220px)' : 'translateX(0)',
      }}>
        {/* Header */}
        <div
          style={{ padding: '12px 14px', background: '#525390', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: '14px' }}
          onClick={onToggle}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
            <FontAwesomeIcon icon={faShip} />
            <span>LNG Vessel Monitor</span>
          </div>
          <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '12px' }} />
        </div>

        {/* Search */}
        <div style={{ padding: '10px', background: 'white', borderBottom: '1px solid #e8e8e8' }}>
          <div style={{ display: 'flex', gap: '6px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', color: '#999', fontSize: '11px' }} />
              <input
                type="text"
                placeholder="Find or add vessel..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '7px 8px 7px 28px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', outline: 'none', background: '#fafafa' }}
              />
            </div>
            <button 
              onClick={() => {
                const newName = prompt('Enter new group name:');
                if (newName && newName.trim() && onCreateGroup) {
                  onCreateGroup(newName.trim());
                }
              }}
              style={{ padding: '7px 10px', background: '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 500 }}
            >
              <FontAwesomeIcon icon={faFolderPlus} />
              Group
            </button>
          </div>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div style={{ margin: '0 10px 8px', background: 'white', border: '1px solid #e8e8e8', borderRadius: '6px', maxHeight: '220px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            {searchResults.map(([id, vessel]) => {
              const name = getVesselName(vessel, id, baseData[id], staticData[id]);
              const isFav = !!favorites[id];
              return (
                <div
                  key={id}
                  style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}
                >
                  <span 
                    style={{ flex: 1, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    onClick={() => { onFlyToVessel(id); setSearchQuery(''); }}
                  >
                    {name} ({id.slice(-5)}...)
                  </span>
                  <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickAdd(id); }}
                      style={{ 
                        width: '26px', height: '26px', 
                        background: isFav ? '#f59e0b' : '#3b82f6', 
                        color: 'white', border: 'none', borderRadius: '4px', 
                        cursor: isFav ? 'default' : 'pointer', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center' 
                      }}
                      title={isFav ? 'Already added' : 'Add to My Favorites'}
                      disabled={isFav}
                    >
                      <FontAwesomeIcon icon={isFav ? faStarSolid : faStarRegular} style={{ fontSize: '12px' }} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openAddToGroupModal(id, name); }}
                      style={{ 
                        width: '26px', height: '26px', 
                        background: '#3b82f6', 
                        color: 'white', border: 'none', borderRadius: '4px', 
                        cursor: 'pointer', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center' 
                      }}
                      title="Add to specific group"
                    >
                      <FontAwesomeIcon icon={faFolder} style={{ fontSize: '11px' }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Favorites List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px', background: '#f8f9fa' }}>
          {Object.keys(favorites).length === 0 && emptyGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 15px', color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FontAwesomeIcon icon={faStarRegular} style={{ fontSize: '28px', marginBottom: '12px', color: '#ccc' }} />
              <p style={{ fontSize: '11px', lineHeight: 1.5 }}>No favorites added yet. Search for vessels above or click the star icon on a vessel popup.</p>
            </div>
          ) : (
            <>
              {/* Show groups with favorites */}
              {sortedGroupEntries.map(([groupName, items]) => (
                <div key={groupName} style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', marginBottom: '6px', borderBottom: '1px solid rgba(82,83,144,0.15)' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#525390', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {groupName === 'default' ? 'My Favorites' : groupName}
                    </span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => onToggleGroupVisibility(groupName)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: hiddenGroups[groupName] ? '#bbb' : '#525390', padding: '2px' }}
                        title={hiddenGroups[groupName] ? 'Show group' : 'Hide group'}
                      >
                        <FontAwesomeIcon icon={hiddenGroups[groupName] ? faEyeSlash : faEye} style={{ fontSize: '11px' }} />
                      </button>
                      {groupName !== 'default' && (
                        <>
                          <button
                            onClick={() => { setRenameGroupName(groupName); setRenameValue(groupName); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '2px' }}
                            title="Rename group"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: '10px' }} />
                          </button>
                          <button
                            onClick={() => handleDeleteGroup(groupName)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '2px' }}
                            title="Delete group"
                          >
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: '10px' }} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {!hiddenGroups[groupName] && items.map((item) => (
                    <div
                      key={item.id}
                      style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', borderRadius: '6px', margin: '4px 0', background: 'white', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', fontSize: '12px' }}
                      onClick={() => onFlyToVessel(item.id)}
                    >
                      <FontAwesomeIcon icon={faStarSolid} style={{ color: '#f59e0b', marginRight: '8px', fontSize: '14px' }} />
                      <span style={{ flex: 1, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                      <button
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setMoveVesselId(item.id); 
                          setMoveVesselName(item.name);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: '2px', marginRight: '4px' }}
                        title="Move to group"
                      >
                        <FontAwesomeIcon icon={faFolder} style={{ fontSize: '10px' }} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onToggleFavorite(item.id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: '2px' }}
                      >
                        <FontAwesomeIcon icon={faXmark} style={{ fontSize: '11px' }} />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
              {/* Show empty groups */}
              {emptyGroups.filter(g => !groupedFavorites[g]).map((groupName) => (
                <div key={groupName} style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', marginBottom: '6px', borderBottom: '1px solid rgba(82,83,144,0.15)' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#525390', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {groupName}
                    </span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => onToggleGroupVisibility(groupName)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: hiddenGroups[groupName] ? '#bbb' : '#525390', padding: '2px' }}
                        title={hiddenGroups[groupName] ? 'Show group' : 'Hide group'}
                      >
                        <FontAwesomeIcon icon={hiddenGroups[groupName] ? faEyeSlash : faEye} style={{ fontSize: '11px' }} />
                      </button>
                      <button
                        onClick={() => { setRenameGroupName(groupName); setRenameValue(groupName); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '2px' }}
                        title="Rename group"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: '10px' }} />
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(groupName)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '2px' }}
                        title="Delete group"
                      >
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: '10px' }} />
                      </button>
                    </div>
                  </div>
                  <div style={{ padding: '12px', color: '#999', fontSize: '11px', fontStyle: 'italic', textAlign: 'center' }}>
                    No vessels in this group
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px', background: 'white', borderTop: '1px solid #e8e8e8' }}>
          <button
            style={{ width: '100%', padding: '10px 14px', background: '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '13px', fontWeight: 500 }}
            onClick={onSwitchToTable}
          >
            <FontAwesomeIcon icon={faTable} />
            List View
          </button>
        </div>
      </aside>

      {/* Expand button when collapsed */}
      {collapsed && (
        <button
          style={{ position: 'absolute', top: '50%', left: 0, transform: 'translateY(-50%)', width: '24px', height: '60px', background: '#525390', color: 'white', border: 'none', borderRadius: '0 6px 6px 0', cursor: 'pointer', zIndex: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}
          onClick={onToggle}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}

      {/* Add to Group Modal */}
      {addToGroupVesselId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', width: '340px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ background: '#525390', color: 'white', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Add {addToGroupVesselName} to Group</h3>
              <button onClick={() => setAddToGroupVesselId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', fontSize: '18px' }}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#333' }}>Select a group to add this vessel to:</p>
              <select
                value={selectedGroup}
                onChange={(e) => {
                  if (e.target.value === '__new__') {
                    const newName = prompt('Enter new group name:');
                    if (newName && newName.trim() && onCreateGroup) {
                      onCreateGroup(newName.trim());
                      setSelectedGroup(newName.trim());
                    }
                  } else {
                    setSelectedGroup(e.target.value);
                  }
                }}
                style={{ 
                  width: '100%', padding: '12px 16px', 
                  background: 'white', border: '1px solid #ddd', borderRadius: '6px', 
                  cursor: 'pointer', fontSize: '14px'
                }}
              >
                {existingGroups.map((group) => (
                  <option key={group} value={group}>
                    {group === 'default' ? 'My Favorites' : group}
                  </option>
                ))}
                <option value="__new__">+ Create New Group</option>
              </select>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button 
                  onClick={() => setAddToGroupVesselId(null)} 
                  style={{ padding: '10px 20px', background: '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAddToGroup} 
                  style={{ padding: '10px 20px', background: '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                >
                  Add to Group
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move to Group Modal */}
      {moveVesselId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', width: '340px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ background: '#525390', color: 'white', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Move {moveVesselName} to Group</h3>
              <button onClick={() => setMoveVesselId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white', fontSize: '18px' }}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#333' }}>Select a group to move this vessel to:</p>
              <select
                value={selectedGroup}
                onChange={(e) => {
                  if (e.target.value === '__new__') {
                    handleMoveToGroup('__new__');
                  } else {
                    setSelectedGroup(e.target.value);
                  }
                }}
                style={{ 
                  width: '100%', padding: '12px 16px', 
                  background: 'white', border: '1px solid #ddd', borderRadius: '6px', 
                  cursor: 'pointer', fontSize: '14px'
                }}
              >
                {existingGroups.map((group) => (
                  <option key={group} value={group}>
                    {group === 'default' ? 'My Favorites' : group}
                  </option>
                ))}
                <option value="__new__">+ Create New Group</option>
              </select>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button 
                  onClick={() => setMoveVesselId(null)} 
                  style={{ padding: '10px 20px', background: '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleMoveToGroup(selectedGroup)} 
                  style={{ padding: '10px 20px', background: '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                >
                  Move to Group
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rename Group Modal */}
      {renameGroupName && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '20px', width: '300px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#525390' }}>Rename Group</h3>
              <button onClick={() => setRenameGroupName(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <input
              type="text"
              placeholder="New group name..."
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRenameGroup()}
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', marginBottom: '16px' }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setRenameGroupName(null)} style={{ padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleRenameGroup} style={{ padding: '8px 16px', background: '#525390', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
